import { Action, getModule, Module, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import cart from './cart';
import { AppEventData, AppLinkData, AppMessageData } from '../models';

@Module({
    namespaced: true,
    name: 'app',
    store,
    dynamic: true,
})
class AppModule extends VuexModule {
    private userAgent = window.navigator.userAgent;
    private appAndroidUserAgent: RegExp = /app-android/;
    private appIosUserAgent: RegExp = /app-ios/;
    private appHybridUserAgent: RegExp = /app-i\b|app-hyb\b/;

    get isApp(): boolean {
        return this.isAndroid || this.isIos;
    }

    get isAndroid(): boolean {
        return this.appAndroidUserAgent.test(this.userAgent);
    }

    get isIos(): boolean {
        return this.appIosUserAgent.test(this.userAgent);
    }

    get isHybrid(): boolean {
        return this.appHybridUserAgent.test(this.userAgent);
    }

    @Action
    public changeCartItemNumber(num?: number) {
        if (this.isApp) {
            const eventData = {
                type: 'cart',
                payload: num === undefined ? cart.cart.items_qty : num,
            };
            this.appEvent(eventData);
        }
    }

    @Action
    public goToHome() {
        if (this.isApp) {
            const linkData = {
                tab: 'STORE_TAB',
            };
            this.goTo(linkData);
        }
    }

    @Action
    public goTo(data: AppLinkData) {
        if (this.isApp) {
            const eventData = {
                type: 'link',
                payload: JSON.stringify({ tab: data.tab, url: data.url === undefined ? null : data.url }),
            };
            this.appEvent(eventData);
        }
    }

    @Action
    public displayMessage(data: AppMessageData) {
        if (this.isApp) {
            const eventData = {
                type: 'message',
                payload: JSON.stringify({ type: data.type, text: data.text }),
            };
            this.appEvent(eventData);
        }
    }

    @Action
    public socialLogin(social: string) {
        if (this.isApp) {
            const eventData = {
                type: `${social}Login`,
                payload: JSON.stringify({}),
            };
            this.appEvent(eventData);
        }
    }

    @Action({ rawError: true })
    private androidEvent(data: AppEventData) {
        if (window.Android !== undefined) {
            switch (data.type) {
                case 'cart':
                    window.Android.notifyCountItemsInCart(data.payload);
                    break;

                case 'message':
                    window.Android.displayUserMessage(data.payload);
                    break;

                case 'link':
                    window.Android.goTo(data.payload);
                    break;

                case 'facebookLogin':
                    window.Android.facebookLogin(data);
                    break;

                case 'googleLogin':
                    window.Android.googleLogin(data);
                    break;

                default:
                    break;
            }
        }
    }

    @Action({ rawError: true })
    private iosEvent(data: AppEventData) {
        if (window.webkit !== undefined) {
            switch (data.type) {
                case 'cart':
                    window.webkit.messageHandlers.notifyCountItemsInCart.postMessage(data.payload);
                    break;

                case 'message':
                    window.webkit.messageHandlers.displayUserMessage.postMessage(data.payload);
                    break;

                case 'link':
                    window.webkit.messageHandlers.goTo.postMessage(data.payload);
                    break;

                case 'facebookLogin':
                    window.webkit.messageHandlers.facebookLogin.postMessage(data);
                    break;

                case 'googleLogin':
                    window.webkit.messageHandlers.googleLogin.postMessage(data);
                    break;

                default:
                    break;
            }
        }
    }

    @Action({ rawError: true })
    private hybridAppEvent(data: AppEventData) {
        switch (data.type) {
            case 'cart':
                if (
                    window.flutter_inappwebview !== undefined &&
                    typeof window.flutter_inappwebview.callHandler === 'function'
                ) {
                    window.flutter_inappwebview.callHandler('notifyCountItemsInCart', data.payload);
                } else {
                    window.addEventListener('flutterInAppWebViewPlatformReady', function() {
                        window.flutter_inappwebview.callHandler('notifyCountItemsInCart', data.payload);
                    });
                }
                break;

            case 'message':
                if (
                    window.flutter_inappwebview !== undefined &&
                    typeof window.flutter_inappwebview.callHandler === 'function'
                ) {
                    window.flutter_inappwebview.callHandler('displayUserMessage', data.payload);
                } else {
                    window.addEventListener('flutterInAppWebViewPlatformReady', function() {
                        window.flutter_inappwebview.callHandler('displayUserMessage', data.payload);
                    });
                }
                break;

            case 'link':
                if (
                    window.flutter_inappwebview !== undefined &&
                    typeof window.flutter_inappwebview.callHandler === 'function'
                ) {
                    window.flutter_inappwebview.callHandler('goTo', data.payload);
                } else {
                    window.addEventListener('flutterInAppWebViewPlatformReady', function() {
                        window.flutter_inappwebview.callHandler('goTo', data.payload);
                    });
                }
                break;

            case 'facebookLogin':
                if (
                    window.flutter_inappwebview !== undefined &&
                    typeof window.flutter_inappwebview.callHandler === 'function'
                ) {
                    window.flutter_inappwebview.callHandler('facebookLogin', data);
                } else {
                    window.addEventListener('flutterInAppWebViewPlatformReady', function() {
                        window.flutter_inappwebview.callHandler('facebookLogin', data);
                    });
                }
                break;

            case 'googleLogin':
                if (
                    window.flutter_inappwebview !== undefined &&
                    typeof window.flutter_inappwebview.callHandler === 'function'
                ) {
                    window.flutter_inappwebview.callHandler('googleLogin', data);
                } else {
                    window.addEventListener('flutterInAppWebViewPlatformReady', function() {
                        window.flutter_inappwebview.callHandler('googleLogin', data);
                    });
                }
                break;

            default:
                break;
        }
    }

    @Action({ rawError: true })
    private appEvent(data: AppEventData) {
        if (this.isHybrid) {
            this.hybridAppEvent(data);
        } else if (this.isAndroid) {
            this.androidEvent(data);
        } else if (this.isIos) {
            this.iosEvent(data);
        }
    }
}

export default getModule(AppModule);
